import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ArrowLeft, ChevronLeft, ChevronRight, Link2, RefreshCw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  UnifiedTable,
  type UnifiedTableColumn,
} from '@/components/ui/unifiedTable/UnifiedTable';

const LINK_ISSUES = gql`
  query OdrCandidateParticipantLinkIssues($page: Int, $pageSize: Int, $search: String) {
    odrCandidateParticipantLinkIssues(page: $page, pageSize: $pageSize, search: $search) {
      rows {
        candidateId
        issueType
        currentParticipantId
        candidateName
        candidateDob
        candidateBooking
        candidateCase
      }
      totalRows
      page
      pageSize
    }
  }
`;

const LINK_ISSUE = gql`
  query OdrCandidateParticipantLinkIssue($candidateId: Int!) {
    odrCandidateParticipantLinkIssue(candidateId: $candidateId) {
      candidateId
      issueType
      currentParticipantId
      candidateName
      candidateDob
      candidateBooking
      candidateCase
      currentParticipant {
        participantId
        odrParticipantId
        name
        dob
        bookings
        reason
      }
      matches {
        participantId
        odrParticipantId
        name
        dob
        bookings
        reason
      }
    }
  }
`;

const REPAIR_LINK = gql`
  mutation RepairOdrCandidateParticipantLink(
    $candidateId: Int!
    $expectedParticipantId: Int
    $participantId: Int!
  ) {
    repairOdrCandidateParticipantLink(
      candidateId: $candidateId
      expectedParticipantId: $expectedParticipantId
      participantId: $participantId
    ) {
      success
      candidateId
      previousParticipantId
      participantId
      lifecycleStage
    }
  }
`;

const REPAIR_MISSING_LINKS = gql`
  mutation RepairMissingOdrCandidateParticipantLinks {
    repairMissingOdrCandidateParticipantLinks {
      scannedCandidates
      repairedCandidates
      skippedCandidates
      failedCandidates
    }
  }
`;

type LinkMatch = {
  participantId: number;
  odrParticipantId: number | null;
  name: string;
  dob: string;
  bookings: string;
  reason: string;
};

type LinkIssue = {
  candidateId: number;
  issueType: string;
  currentParticipantId: number | null;
  candidateName: string;
  candidateDob: string;
  candidateBooking: string;
  candidateCase: string;
  currentParticipant?: LinkMatch | null;
  matches?: LinkMatch[];
};

type IssuePage = {
  rows: LinkIssue[];
  totalRows: number;
  page: number;
  pageSize: number;
};

const PAGE_SIZE = 25;

const issueLabel = (issueType: string): string => {
  if (issueType === 'missing_link') return 'Missing link';
  if (issueType === 'invalid_link') return 'Invalid link';
  if (issueType === 'conflicting_link') return 'Conflicting link';
  return 'Needs review';
};

const Evidence = ({ match }: { match: LinkMatch }) => (
  <div className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
    <div><span className="font-medium">Profile:</span> {match.name || 'Name unavailable'}</div>
    <div><span className="font-medium">ODR number:</span> {match.odrParticipantId || 'Unavailable'}</div>
    <div><span className="font-medium">Participant:</span> {match.participantId}</div>
    <div><span className="font-medium">DOB:</span> {match.dob || 'Unavailable'}</div>
    <div className="sm:col-span-2"><span className="font-medium">Booking:</span> {match.bookings || 'Unavailable'}</div>
  </div>
);

export const OdrCandidateLinkRepairPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [candidateId, setCandidateId] = useState<number | null>(null);
  const [participantId, setParticipantId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [batchConfirming, setBatchConfirming] = useState(false);

  const listQuery = useQuery<{ odrCandidateParticipantLinkIssues: IssuePage }>(LINK_ISSUES, {
    variables: { page, pageSize: PAGE_SIZE, search: submittedSearch },
    fetchPolicy: 'network-only',
  });
  const detailQuery = useQuery<{ odrCandidateParticipantLinkIssue: LinkIssue }>(LINK_ISSUE, {
    variables: { candidateId: candidateId || 0 },
    skip: !candidateId,
    fetchPolicy: 'network-only',
  });
  const [repairLink, repairState] = useMutation(REPAIR_LINK);
  const [repairMissingLinks, missingLinkRepairState] = useMutation(REPAIR_MISSING_LINKS);

  const issuePage = listQuery.data?.odrCandidateParticipantLinkIssues;
  const rows = issuePage?.rows || [];
  const totalRows = issuePage?.totalRows || 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const detail = detailQuery.data?.odrCandidateParticipantLinkIssue;
  const selectedMatch = detail?.matches?.find((match) => match.participantId === participantId) || null;

  useEffect(() => {
    setParticipantId(null);
    setConfirming(false);
  }, [candidateId, detail?.issueType]);

  const columns = useMemo<UnifiedTableColumn<LinkIssue>[]>(() => [
    { key: 'candidateId', header: 'Candidate', field: 'candidateId', sortable: true, dataType: 'int', width: '100px' },
    { key: 'candidateName', header: 'Name', field: 'candidateName', sortable: true },
    { key: 'candidateDob', header: 'DOB', field: 'candidateDob', sortable: true, dataType: 'date', width: '120px' },
    { key: 'candidateBooking', header: 'Booking', field: 'candidateBooking', sortable: true, width: '140px' },
    { key: 'currentParticipantId', header: 'Current participant', field: 'currentParticipantId', sortable: true, dataType: 'int', width: '150px' },
    { key: 'issueType', header: 'Issue', accessor: (row) => issueLabel(row.issueType), sortValue: (row) => row.issueType, sortable: true, width: '140px' },
  ], []);

  const submitSearch = (value: string) => {
    setPage(1);
    setSubmittedSearch(value.trim());
  };

  const confirmRepair = async () => {
    if (!detail || !selectedMatch) return;
    try {
      const response = await repairLink({ variables: {
        candidateId: detail.candidateId,
        expectedParticipantId: detail.currentParticipantId,
        participantId: selectedMatch.participantId,
      } });
      if (!response.data?.repairOdrCandidateParticipantLink?.success) {
        throw new Error('The participant link was not changed.');
      }
      toast.success('Candidate participant link repaired.');
      setConfirming(false);
      setCandidateId(null);
      setParticipantId(null);
      await listQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to repair the participant link.';
      toast.error(message);
      setConfirming(false);
      await Promise.all([listQuery.refetch(), detailQuery.refetch()]);
    }
  };

  const confirmMissingLinkRepair = async () => {
    try {
      const response = await repairMissingLinks();
      const result = response.data?.repairMissingOdrCandidateParticipantLinks;
      if (!result) throw new Error('The missing-link batch did not return a result.');
      toast.success(`Missing-link repair complete: ${result.repairedCandidates} repaired, ${result.skippedCandidates} skipped, ${result.failedCandidates} failed.`);
      setBatchConfirming(false);
      await listQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to repair missing participant links.';
      toast.error(message);
      setBatchConfirming(false);
      await listQuery.refetch();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 text-[var(--neutral-gray-120)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Repair candidate participant links</h1>
          <p className="text-sm text-muted-foreground">Review exact ODR Housing evidence before changing a candidate link.</p>
        </div>
        <Button id="odr-link-repair-back" asChild type="button" variant="outline">
          <Link to="/candidates/odr"><ArrowLeft className="h-4 w-4" />Back to candidates</Link>
        </Button>
      </div>

      {listQuery.error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>Unable to load candidate link issues.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 p-4 pb-2">
          <CardTitle className="text-base">Candidates requiring review</CardTitle>
          <Button
            id="odr-link-repair-open-missing-batch"
            type="button"
            variant="outline"
            disabled={missingLinkRepairState.loading}
            onClick={() => setBatchConfirming(true)}
          >
            <Link2 className="h-4 w-4" />Repair missing links
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 p-4 pt-0">
          <UnifiedTable<LinkIssue>
            columns={columns}
            data={rows}
            rowKey={(row) => String(row.candidateId)}
            loading={listQuery.loading}
            searchEnabled
            clientSearch={false}
            searchValue={search}
            onSearchValueChange={setSearch}
            onSearchSubmit={submitSearch}
            searchPlaceholder="Search candidate, booking, case, or participant"
            emptyTitle="No participant link issues found"
            emptyDescription="No ODR candidates match the current review criteria."
            showRowActions
            rowActionsHeader="Review"
            renderRowActions={(row) => (
              <Button
                id={`odr-link-repair-review-${row.candidateId}`}
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setCandidateId(row.candidateId)}
              >
                <Search className="h-4 w-4" />Review
              </Button>
            )}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>{totalRows ? `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, totalRows)} of ${totalRows}` : 'No issues'}</span>
            <div className="flex items-center gap-2">
              <Button id="odr-link-repair-previous" type="button" variant="outline" size="sm" disabled={page <= 1 || listQuery.loading} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                <ChevronLeft className="h-4 w-4" />Previous
              </Button>
              <span>Page {Math.min(page, totalPages)} of {totalPages}</span>
              <Button id="odr-link-repair-next" type="button" variant="outline" size="sm" disabled={page >= totalPages || listQuery.loading} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>
                Next<ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={candidateId !== null}
        onOpenChange={(open: boolean) => {
          if (open) return;
          setCandidateId(null);
          setParticipantId(null);
          setConfirming(false);
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          className="flex flex-col gap-0 overflow-hidden p-0"
          contentWidth="900px"
          contentHeight="auto"
          contentMaxWidth="calc(100vw - 2rem)"
          contentMaxHeight="90vh"
        >
          <DialogHeader className="flex-row items-center justify-between gap-3 border-b border-[var(--neutral-gray-30)] p-4 pr-12">
            <DialogTitle className="text-base">Candidate {candidateId}</DialogTitle>
            <Button id="odr-link-repair-refresh-evidence" type="button" variant="outline" size="sm" disabled={detailQuery.loading} onClick={() => { void detailQuery.refetch(); }}>
              <RefreshCw className="h-4 w-4" />Refresh evidence
            </Button>
          </DialogHeader>
          <div className="min-h-0 space-y-4 overflow-y-auto p-4">
            {detailQuery.loading && <p role="status" className="text-sm text-muted-foreground">Loading candidate evidence…</p>}
            {detailQuery.error && <Alert variant="destructive" role="alert"><AlertDescription>Unable to load current participant evidence. Refresh the issue list and try again.</AlertDescription></Alert>}
            {detail && (
              <>
                <div className="grid gap-x-4 gap-y-1 rounded-md border border-[var(--neutral-gray-40)] bg-[var(--neutral-gray-10)] p-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <div><span className="font-medium">Candidate:</span> {detail.candidateName}</div>
                  <div><span className="font-medium">DOB:</span> {detail.candidateDob || 'Unavailable'}</div>
                  <div><span className="font-medium">Booking:</span> {detail.candidateBooking || 'Unavailable'}</div>
                  <div><span className="font-medium">Case:</span> {detail.candidateCase || 'Unavailable'}</div>
                  <div><span className="font-medium">Current participant:</span> {detail.currentParticipantId || 'None'}</div>
                  <div><span className="font-medium">Issue:</span> {issueLabel(detail.issueType)}</div>
                </div>

                {detail.currentParticipant && (
                  <div className="space-y-2 rounded-md border border-[var(--semantic-warning)] p-3">
                    <h2 className="text-sm font-semibold">Current link</h2>
                    <Evidence match={detail.currentParticipant} />
                  </div>
                )}

                <div className="space-y-2">
                  <h2 className="text-sm font-semibold">Exact ODR Housing matches</h2>
                  {!detail.matches?.length && (
                    <Alert><AlertDescription>No exact ODR Housing match is available. This record cannot be repaired from this page.</AlertDescription></Alert>
                  )}
                  {detail.matches?.map((match) => (
                    <label key={match.participantId} htmlFor={`odr-link-repair-match-${match.participantId}`} className="flex cursor-pointer gap-3 rounded-md border border-[var(--neutral-gray-40)] p-3 hover:bg-[var(--neutral-gray-10)]">
                      <input
                        id={`odr-link-repair-match-${match.participantId}`}
                        type="radio"
                        name="odr-link-repair-match"
                        className="mt-1"
                        checked={participantId === match.participantId}
                        onChange={() => setParticipantId(match.participantId)}
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="text-sm font-semibold">{match.reason}</div>
                        <Evidence match={match} />
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end border-t border-[var(--neutral-gray-30)] pt-3">
                  <Button id="odr-link-repair-open-confirmation" type="button" disabled={!selectedMatch || repairState.loading} onClick={() => setConfirming(true)}>
                    <Link2 className="h-4 w-4" />Repair participant link
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={batchConfirming} onOpenChange={setBatchConfirming}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Repair all unambiguous missing links?</AlertDialogTitle>
            <AlertDialogDescription>
              Only missing links for candidates marked enrolled or added to DD with exactly one current exact ODR match will be repaired. Missing links with no match or multiple matches will be skipped. Other candidates require individual review. Invalid and conflicting links will not be changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={missingLinkRepairState.loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction id="odr-link-repair-confirm-missing-batch" disabled={missingLinkRepairState.loading} onClick={(event) => { event.preventDefault(); void confirmMissingLinkRepair(); }}>
              <Link2 className="h-4 w-4" />{missingLinkRepairState.loading ? 'Repairing…' : 'Repair missing links'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Repair this participant link?</AlertDialogTitle>
            <AlertDialogDescription>
              Candidate {detail?.candidateId} will be linked to participant {selectedMatch?.participantId}. No participant profile or cross-list record will be changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-1 sm:space-x-0">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction id="odr-link-repair-confirm" disabled={repairState.loading} onClick={(event) => { event.preventDefault(); void confirmRepair(); }}>
              <Link2 className="h-4 w-4" />{repairState.loading ? 'Repairing…' : 'Repair link'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
